import React from "react";
import { ColumnsType } from "antd/es/table";
import { IconButton } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditNoteIcon from "@mui/icons-material/EditNote";
import moment from "moment";
import { ROUTES } from "../../../constants/frontendPaths";

interface DataType {
	paymentId: string;
	partnerName: string;
	sanctionStatus: string;
	actiontaken: number | null;
	actionTakenBy: number | null;
	remark1: string | null;
	remark2: string | null;
	senderSanctiondetailResponse: string;
	receiverSanctiondetailResponse: string;
	paymentStatus: string;
	isExternalComplianceRequired: number;
	creationDate: string;
	partnerId: string;
}

export const hasSanctionResponseData = (jsonString: string | null): boolean => {

	if (!jsonString) return false;

	try {
		const parsedData = JSON.parse(jsonString);

		// ---------- Old Vendor Structure ----------
		const hasPepResults =
			Array.isArray(parsedData?.aml?.pepResults) &&
			parsedData.aml.pepResults.length > 0;

		const hasWlsResults =
			Array.isArray(parsedData?.aml?.wlsResults) &&
			parsedData.aml.wlsResults.length > 0;

		// ---------- New Transvision Structure ----------
		const hasTransvisionResults = (results: any): boolean => {
			return (
				Array.isArray(results) &&
				results.some((item: any) =>
					Array.isArray(item?.result) &&
					item.result.some(
						(r: any) =>
							Number(r?.matchCount) > 0 ||
							(Array.isArray(r?.matched) && r.matched.length > 0)
					)
				)
			);
		};

		const hasSanctionsResults = hasTransvisionResults(parsedData?.sanctions);

		const hasPepResultsNew = hasTransvisionResults(parsedData?.pep);

		return (
			hasPepResults ||
			hasWlsResults ||
			hasSanctionsResults ||
			hasPepResultsNew
		);
	} catch (error) {
		console.error("Error parsing sanction response:", error);
		return false;
	}
};
export const getColumns = (
	handleOpenPopup: (paymentId: string) => void,
	handleRejectPopup1: (paymentId: string) => void,
	note1ClickHandler: (rowData: DataType) => void,
	link1ClickHandler: (rowData: DataType) => void,
	link2ClickHandler: (rowData: DataType) => void,
	approvePayments: any[],
	navigate: (path: string) => void
): ColumnsType<DataType> => {
	const columns: ColumnsType<DataType> = [
		{
			title: "Payment ID",
			dataIndex: "paymentId",
			key: "paymentId",
			render: (text: string, record: DataType) => (
				<a
					onClick={() => navigate(ROUTES.TRANSACTION_DETAILS.replace(":paymentId", text).replace(":partnerId", "1"))}
					style={{
						color: "var(--purple-light)",
						cursor: "pointer",
						textDecoration: "underline"
					}}
				>
					{text}
				</a>
			)
		},
		{
			title: "Created Date",
			dataIndex: "creationDate",
			key: "creationDate",
			sorter: (a, b) => moment(a.creationDate).unix() - moment(b.creationDate).unix(),
			width: "15%",
			render: (text) => moment(text).format("MM/DD/YYYY H:mm.ss")
		},
		{
			title: "Partner Name",
			dataIndex: "partnerName",
			key: "partnerName",
			sorter: (a: DataType, b: DataType) => a.partnerName.localeCompare(b.partnerName)
		},
		{
			title: "Sanction Status",
			dataIndex: "sanctionStatus",
			key: "sanctionStatus",
			render: (status: string) => {
				let imageSrc = "";
				if (status === "GREEN") {
					imageSrc = require("../../../assets/images/GreenStatus.png");
				} else if (status === "AMBER") {
					imageSrc = require("../../../assets/images/YellowStatus.png");
				} else if (status === "RED") {
					imageSrc = require("../../../assets/images/RedStatus.png");
				}

				return (
					<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
						{imageSrc && (
							<img src={imageSrc} alt={status} style={{ width: "20px", height: "20px" }} />
						)}
						{status === "ERROR" && (
							<span style={{ color: "red", fontWeight: 600 }}>ERROR</span>
						)}
					</div>
				);
			}
		},
		{
			title: "Sanction Details",
			key: "sanctionDetails",
			render: (_: any, record: DataType) => {
				const hasSenderData = hasSanctionResponseData(record.senderSanctiondetailResponse);
				const hasReceiverData = hasSanctionResponseData(record.receiverSanctiondetailResponse);
				return (
					<div>
						<a
							style={{
								color: hasSenderData ? "var(--purple-light)" : "#999",
								fontSize: "13px",
								cursor: hasSenderData ? "pointer" : "default",
								textDecoration: hasSenderData ? "underline" : "none",
								pointerEvents: hasSenderData ? "auto" : "none"
							}}
							onClick={() => link1ClickHandler(record)}
						>
							Sender Sanction detail Response
						</a>
						<br />
						<a
							style={{
								color: hasReceiverData ? "var(--purple-light)" : "#999",
								fontSize: "13px",
								cursor: hasReceiverData ? "pointer" : "default",
								textDecoration: hasReceiverData ? "underline" : "none",
								pointerEvents: hasReceiverData ? "auto" : "none"
							}}
							onClick={() => link2ClickHandler(record)}
						>
							Receiver Sanction detail Response
						</a>
					</div>
				);
			}
		},
		{
			title: "Manual Decision",
			key: "manualDecision",
			render: (_: any, record: DataType) => (
				<div className="sactionstyle">
					<IconButton
						color="success"
						disabled={
							(record.actiontaken === null && record.isExternalComplianceRequired === 1) ||
							(record.actiontaken === 0 && record.isExternalComplianceRequired === 0) ||
							(record.actionTakenBy === 0 && record.isExternalComplianceRequired === 1) ||
							(record.actionTakenBy === 0 && record.isExternalComplianceRequired === 0) ||
							(record.actionTakenBy === 1 && record.actiontaken === 0)
						}
						onClick={() => handleOpenPopup(record.paymentId)}
					>
						<CheckIcon />
					</IconButton>
					<IconButton
						sx={{ color: "red" }}
						disabled={
							(record.actiontaken === null && record.isExternalComplianceRequired === 1) ||
							(record.actiontaken === 0 && record.isExternalComplianceRequired === 0) ||
							(record.actionTakenBy === 0 && record.isExternalComplianceRequired === 1) ||
							(record.actionTakenBy === 0 && record.isExternalComplianceRequired === 0) ||
							(record.actionTakenBy === 1 && record.actiontaken === 0)
						}
						onClick={() => handleRejectPopup1(record.paymentId)}
					>
						<CloseIcon />
					</IconButton>
				</div>
			)
		},
		{
			title: "Action Taken",
			dataIndex: "actiontaken",
			key: "actiontaken",
			sorter: (a: DataType, b: DataType) => {
				// Handle null values
				if (a.actiontaken === null && b.actiontaken === null) return 0;
				if (a.actiontaken === null) return -1;
				if (b.actiontaken === null) return 1;
				return a.actiontaken - b.actiontaken;
			},
			render: (action: number | null) => {
				if (action === null) {
					return "Pending";
				} else if (action === 1) {
					return <span style={{ color: "var(--green-primary)" }}>Approved</span>;
				} else if (action === 0) {
					return <span style={{ color: "var(--red-primary)" }}>Rejected</span>;
				} else {
					return "Unknown";
				}
			}
		},
		{
			title: "Notes",
			key: "notes",
			render: (_: any, record: DataType) => (
				<IconButton onClick={() => note1ClickHandler(record)} style={{ marginRight: "10px", display: "flex", alignItems: "center" }}>
					<EditNoteIcon />
				</IconButton>
			)
		}
	];

	return columns;
};
